import accountsData from './accounts.json';
import actionsData from './actions.json';

// ============ HELPERS ============

const AVATAR_COLORS = [
  'bg-blue-600', 'bg-teal-600', 'bg-purple-600', 'bg-orange-600',
  'bg-pink-600', 'bg-indigo-600', 'bg-cyan-600', 'bg-red-600',
  'bg-emerald-600', 'bg-amber-600', 'bg-rose-600', 'bg-sky-600',
  'bg-violet-600', 'bg-slate-600', 'bg-fuchsia-600'
];

function hashStr(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function getInitials(name) {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function getAvatarColor(name) {
  return AVATAR_COLORS[hashStr(name) % AVATAR_COLORS.length];
}

function formatCurrency(val) {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${Math.round(val / 1000)}K`;
  return `$${val}`;
}

// ============ PRE-PROCESSING ============

const actionsByAccountId = {};
actionsData.forEach(action => {
  if (!actionsByAccountId[action.account_id]) {
    actionsByAccountId[action.account_id] = [];
  }
  actionsByAccountId[action.account_id].push(action);
});

export const industries = [...new Set(accountsData.map(a => a.industry))].sort();

// ============ CACHE ============
const cache = {};

// ============ CONFIG ============

const stageConfig = {
  'Commit': { color: 'bg-green-100 text-green-700', detailColor: 'bg-green-50 text-green-600 border-green-200' },
  'Pipeline': { color: 'bg-blue-100 text-blue-700', detailColor: 'bg-blue-50 text-blue-600 border-blue-200' },
  'Renewal': { color: 'bg-purple-100 text-purple-700', detailColor: 'bg-purple-50 text-purple-600 border-purple-200' },
  'Upside': { color: 'bg-yellow-100 text-yellow-700', detailColor: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
};

function deriveStatus(riskDesc) {
  if (!riskDesc) return 'On Track';
  const d = riskDesc.toLowerCase();
  if (d.includes('competitor') || d.includes('evaluate')) return 'Competitor Eval';
  if (d.includes('silent') || d.includes('dormant') || d.includes('ghost')) return 'Ghosting';
  if (d.includes('pricing') || d.includes('budget') || d.includes('objection')) return 'Budget Hold';
  if (d.includes('qbr') || d.includes('adoption') || d.includes('perception')) return 'Adoption Risk';
  if (d.includes('single-threaded') || d.includes('single threaded')) return 'Single Thread';
  if (d.includes('unfamiliar') || d.includes('regression')) return 'Onboarding Risk';
  if (d.includes('gap') || d.includes('push')) return 'Deal Slip';
  return 'At Risk';
}

function toShortSignal(riskDesc) {
  if (!riskDesc) return null;
  const d = riskDesc.toLowerCase();
  if (d.includes('champion went dark') || d.includes('champion silent')) return 'Champion silent';
  if (d.includes('champion left')) return 'Champion left';
  if (d.includes('competitor') || d.includes('competing')) return 'Competitor mentioned';
  if (d.includes('single-threaded') || d.includes('single threaded')) return 'Single threaded';
  if (d.includes('dormant') || d.includes('inactive')) return 'Low engagement';
  if (d.includes('adoption') && d.includes('drop')) return 'Adoption drop';
  if (d.includes('adoption') || d.includes('learner')) return 'Low adoption';
  if (d.includes('csat') || d.includes('ticket spike')) return 'CSAT spike';
  if (d.includes('cash collection') || d.includes('overdue')) return 'Payment overdue';
  if (d.includes('deal stalled') || d.includes('procurement')) return 'Deal stalled';
  if (d.includes('executive sponsor') && d.includes('disengage')) return 'Sponsor disengaged';
  if (d.includes('executive sponsor')) return 'Sponsor risk';
  if (d.includes('unresponsive') || d.includes('no response')) return 'Contact ghosting';
  if (d.includes('pricing') || d.includes('margin') || d.includes('discount')) return 'Pricing pressure';
  if (d.includes('compliance') || d.includes('regulatory')) return 'Compliance gap';
  if (d.includes('integration') && d.includes('block')) return 'Integration blocked';
  if (d.includes('integration') && d.includes('bug')) return 'Integration issues';
  if (d.includes('license utilization')) return 'Underutilized';
  if (d.includes('scope creep') || d.includes('sow scope')) return 'Scope creep';
  if (d.includes('qbr') || d.includes('roi not')) return 'ROI unclear';
  if (d.includes('delivery') || d.includes('milestone')) return 'Delivery risk';
  if (d.includes('data residency') || d.includes('deal-breaker')) return 'Data concern';
  if (d.includes('product gap')) return 'Product gap';
  if (d.includes('renewal') && d.includes('risk')) return 'Renewal risk';
  if (d.includes('sla breach')) return 'SLA breach';
  if (d.includes('resource conflict')) return 'Resource conflict';
  if (d.includes('churn') || d.includes('win-back')) return 'Churn risk';
  if (d.includes('untrained') || d.includes('unfamiliar')) return 'Onboarding gap';
  if (d.includes('msa') || d.includes('legal')) return 'Legal delay';
  if (d.includes('territory') || d.includes('coverage')) return 'Coverage gap';
  if (d.includes('seasonal') || d.includes('window closing')) return 'Window closing';
  if (d.includes('steering') || d.includes('stakeholder')) return 'Misaligned sponsors';
  if (d.includes('attach rate') || d.includes('attach-rate')) return 'Attach rate down';
  if (d.includes('ndc') || d.includes('gds')) return 'Channel shift';
  if (d.includes('hotel-partner') || d.includes('inventory access')) return 'Inventory loss';
  if (d.includes('duty-of-care') || d.includes('duty of care')) return 'Compliance gap';
  if (d.includes('order volume') || d.includes('gmv')) return 'Volume declining';
  if (d.includes('custom content') || d.includes('alternatives')) return 'Feature gap';
  if (d.includes('reference') || d.includes('confidence')) return 'Confidence low';
  if (d.includes('project timeline') || d.includes('phase')) return 'Timeline slipping';
  return 'At risk';
}

const statusColorMap = {
  'Competitor Eval': 'bg-yellow-100 text-yellow-700',
  'Ghosting': 'bg-red-100 text-red-700',
  'Budget Hold': 'bg-orange-100 text-orange-700',
  'Adoption Risk': 'bg-purple-100 text-purple-700',
  'Single Thread': 'bg-red-100 text-red-600',
  'Onboarding Risk': 'bg-blue-100 text-blue-700',
  'Deal Slip': 'bg-amber-100 text-amber-700',
  'At Risk': 'bg-red-100 text-red-600',
  'On Track': 'bg-green-100 text-green-700',
};const contactTitles = [
  'VP of Sales', 'CTO', 'Director of Revenue Ops', 'Head of Procurement',
  'CFO', 'VP of Engineering', 'Product Manager', 'Director of IT',
  'Head of Data Science', 'Legal Counsel', 'VP of Operations',
  'Chief Revenue Officer', 'Head of Partnerships', 'Security Lead'
];

function getQuarter(account, index) {
  const quarters = ['Q1 2026', 'Q2 2026', 'Q3 2026'];
  return quarters[(hashStr(account.name) + index) % quarters.length];
}

function getCloseDate(account) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIdx = (hashStr(account.name) % 6) + 3;
  const day = (hashStr(account.id) % 28) + 1;
  return `Closes ${day} ${months[monthIdx]} 2026`;
}

function getStuckDays(account, riskSeverity) {
  const base = (hashStr(account.name) % 18) + 2;
  if (riskSeverity === 'High') return `${base}d stuck`;
  if (riskSeverity === 'Medium') return `${base + 4}d stuck`;
  return `${base}d`;
}

// ============ MAIN EXPORT ============

export function getIndustryData(industry) {
  if (cache[industry]) return cache[industry];

  const accounts = accountsData
    .filter(a => a.industry === industry)
    .map((account, index) => {
      const actions = actionsByAccountId[account.id] || [];
      const pendingActions = actions.filter(a => a.status === 'Pending');
      const riskActions = pendingActions.filter(a => a.risk_severity);
      const highRiskActions = pendingActions.filter(a => a.risk_severity === 'High');

      return {
        ...account,
        numericId: index + 1,
        actions,
        pendingActions,
        riskActions,
        highRiskActions,
        hasRisk: riskActions.length > 0,
        topRiskSeverity: highRiskActions.length > 0 ? 'High' : riskActions.length > 0 ? 'Medium' : null,
        topRiskDescription: riskActions[0]?.risk_description || null,
      };
    });

  const result = {
    riskMetrics: buildRiskMetrics(accounts),
    dealsPipeline: buildDealsPipeline(accounts),
    opportunities: buildOpportunities(accounts),
    teamMembers: buildTeamMembers(accounts),
    todaysFocus: buildTodaysFocus(accounts),
    accountDetails: buildAccountDetails(accounts),
    industryInsight: buildIndustryInsight(accounts, industry),
  };

  cache[industry] = result;
  return result;
}

// ============ BUILDERS ============

function buildRiskMetrics(accounts) {
  const upsideRisk = accounts.filter(a => a.stage === 'Upside' && a.hasRisk);
  const commitRisk = accounts.filter(a => a.stage === 'Commit' && a.hasRisk);
  const dealsRisk = accounts.filter(a => (a.stage === 'Pipeline' || a.stage === 'Renewal') && a.hasRisk);

  return {
    upsideAtRisk: {
      value: upsideRisk.reduce((sum, a) => sum + a.arr, 0),
      label: 'Upside at risk',
      subtitle: `${upsideRisk.length} accounts flagged`,
      color: 'blue',
    },
    commitAtRisk: {
      value: commitRisk.reduce((sum, a) => sum + a.arr, 0),
      label: 'Commit at risk',
      subtitle: `${commitRisk.length} accounts flagged`,
      color: 'teal',
    },
    dealsAtRisk: {
      value: dealsRisk.reduce((sum, a) => sum + a.arr, 0),
      label: 'Deals at risk',
      subtitle: `${dealsRisk.length} high-priority`,
      color: 'yellow',
    },
  };
}

function buildDealsPipeline(accounts) {
  return accounts
    .filter(a => a.hasRisk)
    .sort((a, b) => b.arr - a.arr)
    .map(account => {
      const topRisk = account.riskActions[0];
      const status = deriveStatus(topRisk?.risk_description);
      let riskCategory;
      if (account.stage === 'Upside') riskCategory = 'upside';
      else if (account.stage === 'Commit') riskCategory = 'commit';
      else riskCategory = 'deals';

      return {
        id: account.numericId,
        companyName: account.name,
        contactName: account.owner_name,
        avatar: getInitials(account.name),
        avatarColor: getAvatarColor(account.name),
        dueIn: getStuckDays(account, account.topRiskSeverity),
        status,
        statusColor: statusColorMap[status] || 'bg-gray-100 text-gray-600',
        dealValue: account.arr,
        actions: account.pendingActions.length,
        riskCategory,
        opportunityId: account.numericId,
      };
    });
}

function buildOpportunities(accounts) {
  return accounts.map((account, index) => {
    const riskSignal = account.topRiskDescription ? toShortSignal(account.topRiskDescription) : null;
    const completedActions = account.actions.filter(a => a.status === 'Completed').length;
    const closedWonDeals = Math.max(1, (hashStr(account.name + 'won') % 9));
    return {
      id: account.numericId,
      accountName: account.name,
      industry: account.industry,
      contactName: account.owner_name,
      avatar: getInitials(account.name),
      avatarColor: getAvatarColor(account.name),
      stage: account.stage,
      stageColor: stageConfig[account.stage]?.color || 'bg-gray-100 text-gray-600',
      riskSignal,
      dealValue: account.arr,
      quarter: getQuarter(account, index),
      closedWonDeals,
    };
  });
}

function buildTeamMembers(accounts) {
  const repMap = {};
  accounts.forEach(account => {
    const name = account.owner_name;
    if (!repMap[name]) {
      repMap[name] = { name, accounts: [], totalPipeline: 0, atRisk: 0, totalActions: 0, completedActions: 0 };
    }
    repMap[name].accounts.push(account);
    repMap[name].totalPipeline += account.arr;
    if (account.hasRisk) repMap[name].atRisk += 1;
    repMap[name].totalActions += account.actions.length;
    repMap[name].completedActions += account.actions.filter(a => a.status === 'Completed').length;
  });

  const regions = ['West', 'East', 'Central', 'EMEA'];
  const roles = ['Rep', 'Rep', 'Rep', 'CSM', 'Manager'];
  const roleColorMap = {
    'Rep': 'bg-green-100 text-green-700',
    'CSM': 'bg-blue-100 text-blue-700',
    'Manager': 'bg-purple-100 text-purple-700',
  };

  return Object.values(repMap).map((rep, index) => {
    const actionRate = rep.totalActions > 0 ? Math.round((rep.completedActions / rep.totalActions) * 100) : 0;
    const role = roles[hashStr(rep.name + 'role') % roles.length];
    return {
      id: index + 1,
      name: rep.name,
      avatar: getInitials(rep.name),
      avatarColor: getAvatarColor(rep.name),
      region: regions[hashStr(rep.name) % regions.length],
      role,
      roleColor: roleColorMap[role] || 'bg-green-100 text-green-700',
      pipeline: rep.totalPipeline,
      dealsCount: rep.accounts.length,
      actionRate,
      actionTrend: actionRate > 50 ? 'up' : 'down',
      atRisk: rep.atRisk || null,
    };
  });
}

function buildTodaysFocus(accounts) {
  const allPendingActions = [];
  accounts.forEach(account => {
    account.pendingActions.forEach(action => {
      allPendingActions.push({ ...action, account });
    });
  });

  const sorted = allPendingActions.sort((a, b) => {
    const priority = { 'High': 0, 'Medium': 1 };
    return (priority[a.risk_severity] ?? 2) - (priority[b.risk_severity] ?? 2);
  });

  const focusActions = sorted.slice(0, 7);

  const todaysActions = focusActions.map((action, index) => {
    let category, badge, borderColor, bgColor, urgency;

    if (action.risk_severity === 'High' && index === 0) {
      category = 'START HERE';
      badge = 'HIGHEST IMPACT';
      borderColor = 'border-l-red-500';
      bgColor = 'bg-red-50/40';
      urgency = null;
    } else if (action.risk_severity === 'High') {
      category = 'START HERE';
      badge = null;
      borderColor = 'border-l-red-300';
      bgColor = 'bg-red-50/30';
      urgency = action.risk_description ? `Risk: ${action.risk_description.slice(0, 50)}` : null;
    } else if (action.risk_severity === 'Medium') {
      category = 'NEEDS FOLLOW-UP';
      badge = null;
      borderColor = 'border-l-transparent';
      bgColor = 'bg-orange-50/40';
      urgency = `Next touch overdue (${(hashStr(action.id) % 10) + 2} days)`;
    } else {
      category = 'UPCOMING';
      badge = null;
      borderColor = 'border-l-transparent';
      bgColor = 'bg-gray-50';
      urgency = null;
    }

    return {
      id: index + 1,
      category,
      badge,
      badgeColor: badge ? 'bg-red-50 text-red-600 border-red-200' : undefined,
      borderColor,
      bgColor,
      title: action.action_text,
      account: action.account.name,
      dealValue: action.account.arr,
      dealTag: action.account.stage === 'Renewal' ? 'Renewal' : 'Deal',
      description: action.risk_description || `Follow up on ${action.action_text.toLowerCase()} for ${action.account.name}.`,
      urgency,
    };
  });

  const actionDetailsMap = {};
  todaysActions.forEach((action, idx) => {
    const rawAction = focusActions[idx];
    const account = rawAction.account;
    const emailName = account.owner_name.split(' ')[0];
    actionDetailsMap[action.id] = {
      aiInsight: rawAction.risk_description
        ? `${rawAction.risk_description}. This action is critical to maintain deal momentum with ${account.name} (${formatCurrency(account.arr)} ARR).`
        : `${account.name} requires attention on this action. Completing it promptly will keep the ${account.stage.toLowerCase()} deal on track and progressing toward close.`,
      contact: {
        name: account.owner_name,
        title: contactTitles[hashStr(account.owner_name) % contactTitles.length],
        email: `${account.owner_name.toLowerCase().replace(/[^a-z]/g, '.').replace(/\.+/g, '.')}@${account.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`,
        avatar: getInitials(account.owner_name),
        avatarColor: getAvatarColor(account.owner_name),
      },
      emailDraft: `Hi ${emailName},\n\nI wanted to follow up regarding ${account.name}. ${rawAction.action_text}.\n\n${rawAction.risk_description ? `Given that ${rawAction.risk_description.toLowerCase()}, I believe acting on this promptly will help keep things on track.` : 'I believe this is the right time to move forward.'}\n\nWould you have time for a quick sync this week?\n\nBest regards`,
    };
  });

  const highImpact = todaysActions.filter(a => a.category === 'START HERE').length;
  const followUps = todaysActions.filter(a => a.category === 'NEEDS FOLLOW-UP').length;

  const metrics = [
    { label: 'High Impact', value: highImpact, color: 'orange' },
    { label: 'Follow-ups Due', value: followUps, color: 'green' },
    { label: 'Opportunities', value: accounts.length, color: 'teal' },
    { label: 'CRM Tasks', value: Math.min(allPendingActions.length, 15), color: 'gray' },
    { label: 'Calls & Meetings', value: Math.min(Math.ceil(accounts.length / 8), 5), color: 'pink' },
  ];

  const scheduledMeetings = accounts.filter(a => a.pendingActions.length > 0).slice(0, 2).map((account, idx) => ({
    id: idx + 1,
    title: idx === 0 ? 'Discovery Meeting' : 'Platform Walkthrough',
    company: account.name,
    contact: account.owner_name,
    description: idx === 0 ? `Discuss pipeline and expansion plans with ${account.name}` : `Review key product features with ${account.name} team`,
    time: idx === 0 ? '10:30 AM' : '2:00 PM',
    duration: idx === 0 ? '30 min' : '45 min',
    type: idx === 0 ? 'call' : 'meeting',
  }));

  const tasksDueToday = accounts.filter(a => a.pendingActions.length > 0).slice(2, 4).map((account, idx) => ({
    id: idx + 1,
    title: idx === 0 ? `Update close date for ${account.name}` : `Add next steps for ${account.name} deal`,
    company: account.name,
    contact: account.owner_name,
    description: idx === 0 ? 'Review and update the projected close date in CRM' : 'Document next steps from latest conversation',
    dueTime: idx === 0 ? 'Today 5:00 PM' : 'Today 6:00 PM',
  }));

  return { metrics, actions: todaysActions, actionDetails: actionDetailsMap, scheduledMeetings, tasksDueToday };
}

function buildAccountDetails(accounts) {
  const details = {};

  accounts.forEach(account => {
    const stage = account.stage;
    const config = stageConfig[stage] || stageConfig['Pipeline'];

    const pendingItems = account.pendingActions.slice(0, 4).map(action => ({
      label: action.action_text.length > 45 ? action.action_text.slice(0, 42) + '...' : action.action_text,
      description: action.risk_description || `Complete this action to keep the ${account.name} deal progressing through the ${stage} stage.`,
    }));

    if (pendingItems.length === 0) {
      pendingItems.push({
        label: 'Review Account Status',
        description: `Review and confirm the current status of the ${account.name} account. No urgent actions pending.`,
      });
    }

    const riskDescs = account.riskActions.map(a => a.risk_description).filter(Boolean);
    const dealSummary = riskDescs.length > 0
      ? `${account.name} is in ${stage} stage with ${formatCurrency(account.arr)} ARR. ${riskDescs[0]}. ${account.pendingActions.length} actions pending, focused attention required to maintain momentum and hit the target close date.`
      : `${account.name} is in ${stage} stage with ${formatCurrency(account.arr)} ARR. The deal is progressing well with ${account.actions.length} logged activities. Continue regular engagement to maintain healthy velocity toward close.`;

    details[account.numericId] = {
      stage,
      stageColor: config.detailColor,
      closeDate: getCloseDate(account),
      poc: {
        name: account.owner_name,
        avatar: getInitials(account.owner_name),
        avatarColor: getAvatarColor(account.owner_name),
      },
      dealSummary,
      pendingActions: {
        items: pendingItems,
        assignedTo: {
          name: account.owner_name,
          role: 'Account Executive',
          avatar: getInitials(account.owner_name),
          avatarColor: getAvatarColor(account.owner_name),
        },
      },
      timeline: generateTimeline(account),
      contacts: generateContacts(account),
    };
  });

  return details;
}

function generateTimeline(account) {
  const events = [];
  let eventId = 1;
  const timeAgo = ['2 days ago', '4 days ago', '1 week ago', '2 weeks ago', '3 weeks ago'];

  account.actions.slice(0, 3).forEach((action, idx) => {
    if (action.status === 'Completed') {
      events.push({
        id: eventId++,
        type: 'stage_change',
        title: `Completed: ${action.action_text}`,
        time: timeAgo[Math.min(idx + 1, timeAgo.length - 1)],
        description: action.risk_description || `Action completed successfully for ${account.name}.`,
      });
    } else {
      events.push({
        id: eventId++,
        type: idx % 2 === 0 ? 'outbound_email' : 'inbound_email',
        title: `${idx % 2 === 0 ? 'Outbound' : 'Inbound'} Email: ${action.action_text}`,
        time: timeAgo[Math.min(idx, timeAgo.length - 1)],
        from: account.owner_name,
        fromAvatar: getInitials(account.owner_name),
        fromAvatarColor: getAvatarColor(account.owner_name),
        description: action.risk_description || `Follow-up regarding ${action.action_text.toLowerCase()}.`,
      });
    }
  });

  events.push({
    id: eventId++,
    type: 'stage_change',
    title: `Stage Changed to '${account.stage}'`,
    time: '1 week ago',
    description: `Deal moved to ${account.stage} stage based on recent activity and stakeholder feedback.`,
  });

  events.push({
    id: eventId++,
    type: 'meeting',
    title: 'Meeting: Account Review',
    time: '2 weeks ago',
    description: `Reviewed account status and alignment on next steps with the ${account.name} team.`,
  });

  return events;
}

function generateContacts(account) {
  const contacts = [];
  let contactId = 1;

  contacts.push({
    id: contactId++,
    name: account.owner_name,
    title: contactTitles[hashStr(account.owner_name) % contactTitles.length],
    avatar: getInitials(account.owner_name),
    avatarColor: getAvatarColor(account.owner_name),
    badge: 'Champion',
  });

  const extraContacts = [
    ['Alex Rivera', 'Head of Procurement'],
    ['Morgan Walsh', 'Director of IT Operations'],
    ['Jordan Blake', 'General Counsel'],
    ['Casey Lin', 'VP of Product'],
  ];

  const numExtra = (hashStr(account.name) % 3) + 1;
  for (let i = 0; i < numExtra; i++) {
    const [name, title] = extraContacts[(hashStr(account.id) + i) % extraContacts.length];
    contacts.push({
      id: contactId++,
      name,
      title,
      avatar: getInitials(name),
      avatarColor: getAvatarColor(name + account.name),
    });
  }

  return contacts;
}

function buildIndustryInsight(accounts, industry) {
  const totalArr = accounts.reduce((sum, a) => sum + a.arr, 0);
  const atRiskAccounts = accounts.filter(a => a.hasRisk);
  const totalPendingActions = accounts.reduce((sum, a) => sum + a.pendingActions.length, 0);

  const upsideRisk = accounts.filter(a => a.stage === 'Upside' && a.hasRisk);
  const commitRisk = accounts.filter(a => a.stage === 'Commit' && a.hasRisk);
  const renewalAccounts = accounts.filter(a => a.stage === 'Renewal');
  const renewalAtRisk = renewalAccounts.filter(a => a.hasRisk);
  const renewalHealthy = renewalAccounts.length - renewalAtRisk.length;

  const riskDescriptions = atRiskAccounts.flatMap(a => a.riskActions.map(r => r.risk_description)).filter(Boolean);
  const patterns = {};
  riskDescriptions.forEach(desc => {
    const key = deriveStatus(desc);
    patterns[key] = (patterns[key] || 0) + 1;
  });
  const topPattern = Object.entries(patterns).sort((a, b) => b[1] - a[1])[0];

  let summary = `Your ${industry} portfolio spans ${accounts.length} accounts with ${formatCurrency(totalArr)} in combined ARR. `;
  if (atRiskAccounts.length > 0) {
    const parts = [];
    if (upsideRisk.length > 0) parts.push(`${upsideRisk.length} in Upside (${formatCurrency(upsideRisk.reduce((s, a) => s + a.arr, 0))})`);
    if (commitRisk.length > 0) parts.push(`${commitRisk.length} in Commit (${formatCurrency(commitRisk.reduce((s, a) => s + a.arr, 0))})`);
    summary += `${atRiskAccounts.length} accounts show risk signals${parts.length > 0 ? ' \u2014 ' + parts.join(' and ') + ' require attention' : ''}. `;
  }
  if (topPattern) {
    summary += `Top pattern: ${topPattern[0].toLowerCase()} across ${topPattern[1]} deals. `;
  }
  if (renewalAccounts.length > 0) {
    summary += `${renewalHealthy} of ${renewalAccounts.length} renewals are on track.`;
  }

  return {
    totalAccounts: accounts.length,
    totalArr,
    atRiskCount: atRiskAccounts.length,
    totalPendingActions,
    renewalHealthy,
    renewalTotal: renewalAccounts.length,
    summary: summary.trim(),
  };
}
